export type CheeseTraits = Record<string, string | boolean>
export type CheeseEventProperties = Record<string, string | number | boolean>

export interface CheeseSdk {
    init: (key: string, options: { host: string; app: string; autoPage: boolean; respectDNT: boolean; replay: false }) => void
    identify: (id: string, traits: CheeseTraits) => void
    userId: () => string | null
    reset: () => void
    optOut: () => void
    optIn: () => void
    hasOptedOut: () => boolean
    page: () => void
    track: (name: string, properties?: CheeseEventProperties) => void
}

export interface CheeseTrackingState {
    allowed: boolean
    authReady: boolean
    userId: string | null
    traits: CheeseTraits
    path: string
}

/** One controller owns consent, identity and page views, including while the SDK loads. */
export function createCheeseTracking(options: {
    host: string
    app: string
    writeKey: string
    load: () => Promise<CheeseSdk>
    onError: (error: unknown) => void
}) {
    let state: CheeseTrackingState | undefined
    let sdk: CheeseSdk | undefined
    let loading: Promise<void> | undefined
    let initialized = false
    let active = false
    let signature: string | undefined
    let lastPage: string | undefined
    let disposed = false

    const apply = () => {
        if (!sdk || !state || disposed) return

        if (!state.allowed) {
            if (initialized) sdk.optOut()
            active = false
            signature = undefined
            lastPage = undefined
            return
        }

        // Firebase briefly enters a loading state during login/profile hydration.
        // Preserve the visitor ID so identify can link the pre-login journey.
        if (!state.authReady) return

        if (!initialized) {
            sdk.init(options.writeKey, { host: options.host, app: options.app, autoPage: false, respectDNT: true, replay: false })
            initialized = true
        }
        if (!active) sdk.optIn()
        if (sdk.hasOptedOut()) return
        active = true

        // A shared browser or expired Firebase session must not inherit the previous user.
        const previousUser = sdk.userId()
        if (previousUser !== state.userId && previousUser) {
            sdk.reset()
            signature = undefined
            lastPage = undefined
        }

        const nextSignature = JSON.stringify([state.userId, state.traits])
        if (state.userId && signature !== nextSignature) sdk.identify(state.userId, state.traits)
        signature = nextSignature

        const page = JSON.stringify([state.path, state.userId])
        if (page !== lastPage) {
            sdk.page()
            lastPage = page
        }
    }

    return {
        async track(name: string, properties: CheeseEventProperties = {}) {
            if (!state?.allowed || !state.authReady || disposed) return
            const userId = state.userId
            try {
                await loading
                if (!sdk || !active || disposed || !state.allowed || !state.authReady || state.userId !== userId) return
                if (!sdk.hasOptedOut()) sdk.track(name, properties)
            } catch (error) {
                options.onError(error)
            }
        },
        sync(next: CheeseTrackingState) {
            state = next
            if (disposed) return
            if (sdk) {
                apply()
            } else if (next.allowed && next.authReady && !loading) {
                loading = options
                    .load()
                    .then((loaded) => {
                        sdk = loaded
                        apply()
                    })
                    .catch(options.onError)
                    .finally(() => {
                        loading = undefined
                    })
            }
        },
        dispose() {
            disposed = true
            if (initialized) sdk?.optOut()
        }
    }
}
